import React, { useState, useEffect } from 'react';
const toNum = (v: any) => Number(v) || 0;

// Auth helper
const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('xnuup_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};

// Types
interface ETLBatch {
  batch_id: number;
  batch_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  source_rows: number;
  inserted_rows: number;
  updated_rows: number;
  duplicate_rows: number;
  error_rows: number;
  insert_rate_pct: number | null;
  duplicate_rate_pct: number | null;
  error_message: string | null;
  hostname: string | null;
  etl_version: string | null;
}

interface ETLSummary {
  total_batches: number;
  successful_batches: number;
  failed_batches: number;
  avg_duration_seconds: number;
  total_source_rows: number;
  total_inserted_rows: number;
  total_duplicate_rows: number;
  total_error_rows: number;
  overall_duplicate_rate: number;
  last_run: string;
  first_run: string;
}

interface ETLTrend {
  run_date: string;
  batch_count: number;
  success_count: number;
  source_rows: number;
  inserted_rows: number;
  duplicate_rows: number;
  error_rows: number;
  avg_duration: number;
}

interface ETLError {
  error_type: string;
  severity: string;
  error_count: number;
  affected_batches: number;
}

interface ETLDuplicate {
  duplicate_type: string;
  duplicate_count: number;
  affected_batches: number;
  unique_claims: number;
}

interface ETLData {
  batches: ETLBatch[];
  summary: ETLSummary;
  trend: ETLTrend[];
  errors: ETLError[];
  duplicates: ETLDuplicate[];
}

// Sort configuration
interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const API_BASE = '/api';

const ETLDashboard: React.FC = () => {
  const [data, setData] = useState<ETLData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'started_at', direction: 'desc' });
  const [activeTab, setActiveTab] = useState<'overview' | 'batches' | 'errors' | 'duplicates'>('overview');

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/etl/monitoring?days=${days}`);
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      } else {
        setError(json.error || 'Failed to load ETL data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = <T extends Record<string, any>>(items: T[], key: string, direction: 'asc' | 'desc'): T[] => {
    return [...items].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string') {
        return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const SortHeader: React.FC<{ label: string; sortKey: string }> = ({ label, sortKey }) => (
    <th
      onClick={() => handleSort(sortKey)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey && (
          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  const formatDateTime = (dt: string | null) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString();
  };

  const formatDate = (dt: string | null) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString();
  };

  const formatNumber = (n: number | null) => {
    if (n === null || n === undefined) return '-';
    return n.toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      partial: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <strong>Error:</strong> {error}
        <button onClick={fetchData} className="ml-4 text-red-600 underline">Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, batches, trend, errors, duplicates } = data;
  const sortedBatches = sortData(batches, sortConfig.key, sortConfig.direction);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">ETL Execution Monitor</h2>
        <div className="flex items-center gap-4">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Batches</div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(summary.total_batches)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Success Rate</div>
          <div className="text-2xl font-bold text-green-600">
            {summary.total_batches > 0
              ? ((summary.successful_batches / summary.total_batches) * 100).toFixed(1)
              : 0}%
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Avg Duration</div>
          <div className="text-2xl font-bold">{Number(summary.avg_duration_seconds || 0).toFixed(1)}s</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Inserted</div>
          <div className="text-2xl font-bold text-blue-600">{formatNumber(summary.total_inserted_rows)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Duplicate Rate</div>
          <div className="text-2xl font-bold text-yellow-600">{summary.overall_duplicate_rate || 0}%</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Errors</div>
          <div className="text-2xl font-bold text-red-600">{formatNumber(summary.total_error_rows)}</div>
        </div>
      </div>

      {/* Last Run Info */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-gray-500">Last ETL Run:</span>
            <span className="ml-2 font-medium">{formatDateTime(summary.last_run)}</span>
          </div>
          <div>
            <span className="text-gray-500">Period:</span>
            <span className="ml-2 font-medium">{formatDateTime(summary.first_run)} - {formatDateTime(summary.last_run)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {(['overview', 'batches', 'errors', 'duplicates'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Trend Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Daily Trend</h3>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Batches</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inserted</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Duplicates</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trend.map((t, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{formatDate(t.run_date)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">
                        {t.batch_count} ({t.success_count} ok)
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(t.inserted_rows)}</td>
                      <td className="px-4 py-2 text-sm text-right text-yellow-600">{formatNumber(t.duplicate_rows)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{t.avg_duration}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Batches */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Batches</h3>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inserted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {batches.slice(0, 10).map((b) => (
                    <tr key={b.batch_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{b.batch_id}</td>
                      <td className="px-4 py-2 text-sm">{getStatusBadge(b.status)}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{formatDateTime(b.started_at)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(b.inserted_rows)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader label="Batch ID" sortKey="batch_id" />
                  <SortHeader label="Type" sortKey="batch_type" />
                  <SortHeader label="Status" sortKey="status" />
                  <SortHeader label="Started" sortKey="started_at" />
                  <SortHeader label="Duration" sortKey="duration_seconds" />
                  <SortHeader label="Source" sortKey="source_rows" />
                  <SortHeader label="Inserted" sortKey="inserted_rows" />
                  <SortHeader label="Duplicates" sortKey="duplicate_rows" />
                  <SortHeader label="Errors" sortKey="error_rows" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Message</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBatches.map((b) => (
                  <tr key={b.batch_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{b.batch_id}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{b.batch_type}</td>
                    <td className="px-4 py-2 text-sm">{getStatusBadge(b.status)}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(b.started_at)}</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-900">{Number(b.duration_seconds || 0).toFixed(1)}s</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(b.source_rows)}</td>
                    <td className="px-4 py-2 text-sm text-right text-blue-600">{formatNumber(b.inserted_rows)}</td>
                    <td className="px-4 py-2 text-sm text-right text-yellow-600">{formatNumber(b.duplicate_rows)}</td>
                    <td className="px-4 py-2 text-sm text-right text-red-600">{formatNumber(b.error_rows)}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate" title={b.error_message || ''}>
                      {b.error_message || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errors Tab */}
      {activeTab === 'errors' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {errors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No errors recorded in this period</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Affected Batches</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {errors.map((e, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{e.error_type}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          e.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          e.severity === 'error' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {e.severity}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(e.error_count)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(e.affected_batches)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Duplicates Tab */}
      {activeTab === 'duplicates' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {duplicates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No duplicates recorded in this period</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duplicate Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Affected Batches</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unique Claims</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {duplicates.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{d.duplicate_type}</td>
                      <td className="px-4 py-2 text-sm text-right text-yellow-600">{formatNumber(d.duplicate_count)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(d.affected_batches)}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(d.unique_claims)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ETLDashboard;